import React from 'react';

/**
 * MemberAvatar — the canonical avatar for any SLL BriteHub member.
 *
 *  • If the member has a real picture, it renders the <img>.
 *  • If the picture endpoint returns 404 (no photo), the dual-layer
 *    pattern falls back to a brand gradient (blue → navy → sage) with
 *    the member's first initial in bold black — a "genuine looking"
 *    avatar instead of a blank circle.
 *
 *  Use this everywhere a member avatar appears so the entire platform
 *  has a single consistent treatment. Drop-in across:
 *    – Dashboard New Members widget
 *    – MemberDirectory cards
 *    – MessagesPage sidebar
 *    – Forum post authors
 *    – Event attendees, BriteLab participants, etc.
 *
 *  Props:
 *    name          required — used for the initial + alt text
 *    picture       optional — image src (URL or /api/members/{id}/avatar)
 *    size          number  — width/height in px (default 40)
 *    className     additional classes for the outer wrapper
 *    textClassName override classes on the initial (e.g. text size)
 *    onClick       optional click handler
 *
 *  data-testid is auto-set from props.testId if supplied.
 */
export default function MemberAvatar({
  name,
  picture,
  size = 40,
  className = '',
  textClassName = '',
  onClick,
  testId,
  ringClassName = '',
}) {
  const dim = `${size}px`;
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  // Scale the initial roughly proportional to the size so the letter
  // reads cleanly at all sizes without consumers having to fiddle.
  const fontClass = size <= 28 ? 'text-xs' : size <= 40 ? 'text-sm' : size <= 56 ? 'text-base' : 'text-lg';
  return (
    <div
      onClick={onClick}
      data-testid={testId}
      className={`relative rounded-full overflow-hidden flex-shrink-0 ${className} ${ringClassName}`}
      style={{ width: dim, height: dim }}
    >
      <div
        className={`absolute inset-0 rounded-full bg-gradient-to-br from-brand-blue via-brand-navy to-brand-sage flex items-center justify-center font-bold text-slate-900 select-none ${fontClass} ${textClassName}`}
        aria-hidden="true"
      >
        {initial}
      </div>
      {picture ? (
        <img
          src={picture}
          alt={name || ''}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover rounded-full"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : null}
    </div>
  );
}
